<template>
  <div
    class="inline-flex items-stretch justify-center rounded-md text-sm font-medium transition-colors duration-200 focus:outline-none select-none border border-solid"
    :class="[
      disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      containerClasses,
      buttonHeightClasses,
    ]"
    :disabled="disabled"
    :data-test-id="dataTestId"
  >
    <!-- Left icon if provided -->
    <button
      v-if="iconLeft"
      class="outline-transparent outline focus:outline-2 focus:outline-offset-2 rounded-md inline-flex items-center justify-center font-medium transition-colors duration-200"
      :class="[buttonSizeClasses, buttonSectionClasses, disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:brightness-110']"
      :data-test-id="generateDataTestId(dataTestId, 'split-button', 'left')"
      @click="emit('click-left')"
    >
      <AM3Icon
        :key="`left-${iconLeft}`"
        :type="iconLeft"
        :size="iconSize"
        color="currentColor"
      />
    </button>

    <!-- Left separator if enabled and icon is present -->
    <div
      v-if="iconLeft"
      class="w-px my-auto cursor-default opacity-30"
      :class="[separatorHeightClasses, separatorColorClass]"
    />

    <!-- Button text -->
    <button
      v-if="text"
      class="outline-transparent outline focus:outline-2 focus:outline-offset-2 rounded-md inline-flex items-center justify-center font-medium transition-colors duration-200"
      :class="[buttonSizeClasses, buttonSectionClasses, disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:brightness-110']"
      :data-test-id="generateDataTestId(dataTestId, 'split-button', 'main')"
      @click="emit('click-text')"
    >
      <AM3Icon
        v-if="iconTextLeft"
        :key="`text-left-${iconTextLeft}`"
        :type="iconTextLeft"
        :size="iconSize"
        color="currentColor"
        class="mr-1"
      />
      <span class="px-1">{{ text }}</span>
      <AM3Icon
        v-if="iconTextRight"
        :key="`text-right-${iconTextRight}`"
        :type="iconTextRight"
        :size="iconSize"
        color="currentColor"
        class="ml-1"
      />
    </button>

    <!-- Right separator if enabled and icon is present -->
    <div
      v-if="iconRight"
      class="w-px my-auto cursor-default opacity-30"
      :class="[separatorHeightClasses, separatorColorClass]"
    />

    <!-- Right icon if provided -->
    <button
      v-if="iconRight"
      class="outline-transparent outline focus:outline-2 focus:outline-offset-2 rounded-md inline-flex items-center justify-center font-medium transition-colors duration-200"
      :class="[buttonSizeClasses, buttonSectionClasses, disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:brightness-110']"
      :data-test-id="generateDataTestId(dataTestId, 'split-button', 'right')"
      @click="emit('click-right')"
    >
      <AM3Icon
        :key="`right-${iconRight}`"
        :type="iconRight"
        :size="iconSize"
        color="currentColor"
      />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AM3Icon from './icon/AM3Icon.vue'
import type { IconSize } from './icon/AM3Icon.vue'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'

export type ButtonType = 'primary' | 'secondary' | 'tertiary'
export type ButtonSize = 'md' | 'sm' | 'xs'

interface Props {
  text: string
  type?: ButtonType
  size?: ButtonSize
  disabled?: boolean
  iconLeft?: string
  iconRight?: string
  iconTextLeft?: string
  iconTextRight?: string
  dataTestId?: string
}

const props = withDefaults(defineProps<Props>(), {
  type: 'primary',
  size: 'md',
  disabled: false,
  iconLeft: '',
  iconRight: '',
  iconTextLeft: '',
  iconTextRight: '',
  dataTestId: props => props.text,
})

const emit = defineEmits<{
  'click-left': []
  'click-text': []
  'click-right': []
}>()

// Determine icon size based on button size
const iconSize = computed((): IconSize => {
  if (props.size === 'xs') return 'sm'
  return 'sm'
})

// Compute button classes based on size
const buttonSizeClasses = computed(() => {
  switch (props.size) {
    case 'xs':
      return 'max-h-6 px-2 py-0.5 text-xs h-full'
    case 'sm':
      return 'max-h-9 px-2 py-1.5 text-sm'
    default:
      return 'max-h-11 px-2.5 py-2 text-sm'
  }
})

const buttonHeightClasses = computed(() => {
  switch (props.size) {
    case 'xs':
      return 'h-6'
    case 'sm':
      return 'h-9'
    default:
      return 'h-11'
  }
})

const separatorHeightClasses = computed(() => {
  switch (props.size) {
    case 'xs':
      return 'h-4'
    case 'sm':
      return 'h-7'
    default:
      return 'h-9'
  }
})

// Container and button section classes based on type
const containerClasses = computed(() => {
  // if (props.disabled) {
  switch (props.type) {
    case 'primary':
      return 'bg-brand  outline-1 text-white border-brand-disabled'
    case 'secondary':
      return 'bg-surface-light border-brand text-font-disabled border-border-strong'
    case 'tertiary':
      return 'bg-surface-disabled text-font-disabled border-border-strong'
    default:
      return 'bg-brand-disabled text-white border-brand-disabled'
    // }
  }
})

const buttonSectionClasses = computed(() => {
  if (props.disabled) {
    switch (props.type) {
      case 'primary':
        return 'bg-brand-disabled text-white'
      case 'secondary':
        return 'bg-surface-light text-font-disabled'
      case 'tertiary':
        return 'bg-surface-disabled text-font-disabled'
      default:
        return 'bg-brand-disabled text-white'
    }
  }

  switch (props.type) {
    case 'primary':
      return 'bg-brand text-white focus:outline-brand hover:bg-brand-hover'
    case 'secondary':
      return 'bg-surface-light focus:outline-surface-dark text-brand hover:bg-surface-hover'
    case 'tertiary':
      return 'bg-surface-light focus:outline-surface-dark text-font-primary hover:bg-surface-hover'
    default:
      return 'bg-brand text-white hover:bg-brand-hover'
  }
})

const separatorColorClass = computed(() => {
  return 'bg-current'
})
</script>
